import React, { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import Spreadsheet, { Model, createFormulaParser } from "react-spreadsheet";
import parse from "html-react-parser";
import * as XLSX from "xlsx";
import { Button, Spinner } from "react-bootstrap";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Download,
  FileText,
  Globe2,
  RefreshCw,
  ShieldCheck,
  Table2,
  Trash2,
} from "lucide-react";
import { getAuthenticatedWebSocketUrl, getUserId } from "../../../utilities/authHelper";
import "./reportAnalysis.css";
import { useResolvedDark } from "../../../ThemeProvider/ThemeContext";

const REPORT_KIND = "M&A Accretion / Dilution";
const CREDIT_REPORT_KIND = "Public Company Credit Screening & Early-Warning Snapshot";
const CREDIT_REPORT_QUERY = "Prepare a public company credit screening and early-warning snapshot";
const GEOGRAPHICAL_EXPANSION_REPORT_KIND = "Geographical Expansion";
const REPORT_KINDS = [
  {
    value: "ma",
    label: REPORT_KIND,
    detail: "Accretion / dilution analysis",
    icon: Building2,
    requiresTarget: true,
  },
  {
    value: "credit",
    label: CREDIT_REPORT_KIND,
    detail: "Credit and early-warning snapshot",
    icon: ShieldCheck,
    requiresTarget: false,
  },
  {
    value: "geographical-expansion",
    label: GEOGRAPHICAL_EXPANSION_REPORT_KIND,
    detail: "Geographical expansion assessment",
    icon: Globe2,
    requiresTarget: false,
  },
];
const normalizeCompanyKey = (companyName) =>
  String(companyName || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\b(limited|ltd)\b/g, "")
    .replace(/[^a-z0-9]/g, "");

// This is the source of truth for the target labels shown in the UI and the
// slugs sent to ReportGPT. Labels must match TARGET_CANDIDATE_MAPPINGS exactly.
const TARGET_SLUGS = Object.fromEntries(
  `
Onafriq|onafriq
Carry1st|adg-technology-inc
Smile Identity|smile-identity
OmniRetail|omniretail
Wasoko-MaxAB|wasoko
Float|float-df0d
Earth AI|earth-ai
Fleet Space Technologies|fleet-space-technologies
Novamera|novamera
LifeQ|lifeq
Quro Medical|quro-medical
AURA|aura-dae7
Yoco|yoco
Lula|lulalend
Carbon Clean|carbon-clean-solutions
Twelve|twelve-co
Mombak|mombak
Boston Metal|boston-metal
Allonnia|allonnia
Celcoin|celcoin
Creditas|creditas
Nomad|nomad-deb4
Clara|clara-fintech
Delfos Energy|delfos-intelligent-maintenance
Lemon Energia|lemon-energy
Solfácil|solfácil
Klook|klook
KKday|kkday
GetYourGuide|getyourguide
Neolix|neolix-neolithic-century-technology
Pudu Robotics|pudutech
Keenon Robotics|keenon-robotics
DeepRoute.ai|deeproute
Momenta|momenta-ai
Hai Robotics|hai-robotics
Quicktron|quicktron-robotics
The CareVoice|the-carevoice
MediTrust Health|meditrust-health
Insgeek|insgeek
Rebellions|rebellions-ai
DEEPX|deepx-c937
FuriosaAI|furiosaai
RideFlux|rideflux
bitsensing|bitsensing
Twelve Labs|twelve-labs-62b5
Upstage|upstage-ai
Wrtn Technologies|wrtn-technologies
Perigee Aerospace|perigee-aerospace
Nearthlab|nearthlab
PABLO AIR|pablo-air
Avicena|avicena-tech
Ayar Labs|ayar-labs
M2P Fintech|m2p-fintech
Signzy|signzy
Perfios|perfios-software-solutions-pvt-ltd
Fibe|earlysalary
Jupiter|amica-financial-technologies
FinBox|finbox
Fourth Partner Energy|fourth-partner-energy
Aerem|aerem-c36d
AmpereHour Energy|ampere-hour-energy
Open|open-5
Hygenco|hygenco
Newtrace|newtrace
Rebel Foods|faaso-s
Curefoods|curefoods
Porter|the-porter
Cashfree Payments|cashfree
The Whole Truth|and-nothing-else
Slurrp Farm|slurrp-farm
Bombay Shaving Company|bombay-shaving-company
Euler Motors|euler-motors
Vecmocon|vecmocon-technologies
Exponent Energy|exponent-energy
Whatfix|whatfix-2
Uniphore|uniphore
Yellow.ai|yellow-messenger
Darwinbox|darwinbox
Pilgrim|pilgrim-9488
Foxtale|foxtale
EdgeGrid|edgegrid
Kazam|kazam-724b
Naked Insurance|naked-insurance
Pineapple|pineapple
Stitch|stitch
Omnisient|omnisient
Plotlogic|plotlogic
VerAI Discoveries|verai-discoveries
KoBold Metals|kobold-metals
Peach Payments|peach-payments
Tyme Group|tyme-group
Soul App|soul-app
Xiaohongshu (RedNote)|xiaohongshu
MiniMax|minimax-a48a
Cider|cider-b40f
XTransfer|xtransfer
Fenbeitong|fenbeitong
Qupital|qupital
M Stand|m-stand
HeyTea|heytea
Rokid|rokid
Hithium|hithium
Envision Energy|envision-energy-83a0
Game Science|game-science
Lilith Games|lilith-games
Paper Games|papergames
MediLink Therapeutics|medilink-therapeutics
Immorna|immorna
Accutar Biotechnology|accutarbio
StepFun|stepstar
Shoplazza|shoplazza
Moonshot AI|moonshot-ai
Sigenergy|sigenergy
Jiangxiaobai|jiangxiaobai
Genki Forest|genki-forest
SiliconFlow|silicon-mobile-technology
ModelBest|mianbi-smart
Airwallex|airwallex
Seoul Robotics|seoul-robotics
Korea Credit Data|korea-credit-data
Viva Republica / Toss|toss
Qraft Technologies|qraft-technologies
Galux|galux
Standigm|standigm
Sovargen|sovargen
Helsing|helsing
Exotrail|exotrail
Unseenlabs|unseenlabs
Qonto|qonto
Swan|swan
Pennylane|pennylane
KODE Labs|kode-labs
Sensorberg|sensorberg
enVerid Systems|enverid
GitGuardian|gitguardian
Actility|actility
Celonis|celonis
Parloa|parloa
Dataiku|dataiku
Owkin|owkin
Insilico Medicine|insilico-medicine
Generate Biomedicines|generate-biomedicines
Sublime Systems|sublime-systems
Brimstone|brimstone-energy
Trade Republic|trade-republic
Raisin|raisin-weltsparen
Scalable Capital|scalable-capital
SEON|seon-security
Barion|barion-payment-inc
PastPay|pastpay
Udaan|udaan
Shiprocket|shiprocket
Immuneel Therapeutics|immuneel-therapeutics
Eyestem|eyestem
Qure.ai|qure-ai
CloudSEK|cloudsek
Neysa|neysa
QNu Labs|qunu
Infra.Market|infra-market
DeHaat|dehaat
Xendit|xendit
Mekari|mekari
Paper.id|paper-id
Amartha|amartha
AwanTunai|awantunai
Funding Societies / Modalku|funding-societies
Sakana AI|sakana-ai
Preferred Networks|preferred-networks
TIER IV|tier-iv
Telexistence|telexistence-inc
Mujin|mujin
proteanTecs|proteantecs
EdgeCortix|edgecortix
Elix|elix
Tap Payments|tap-payments
Tarabut|tarabut-gateway
NymCard|nymcard
Wahed|wahed-invest-inc
Funding Societies|funding-societies
PolicyStreet|policystreet
CapBay|capital-bay
MoneyMatch|moneymatch
Carsome|carsome
Ditrolic Energy|ditrolic-energy
Plus Xnergy|plus-xnergy
VFlowTech|vflow-tech
Jüsto|justo
99minutos|99-minutos
Yalo|yalo
Clip|payclip
Stori|stori
Belvo|belvo
MineSense|minesense-technologies
Silent Eight|silent-eight-search
Autenti|autenti
ZEN.COM|zen-com-uab
OxCCU|oxccu
Hysata|hysata
Tamara|tamara
Lean Technologies|lean-technologies
Lendo|lendo-bdcc
Dioxycle|dioxycle
Unifonic|unifonic
Lucidya|lucidya
Mozn|mozn
CLARK|clark
Getsafe|getsafe
dacadoo|dacadoo
Moonfare|moonfare
iCapital|icapital-network
AssetMetrix|assetmetrix
Formo|formo
Remilk|remilk
Onego Bio|onego-bio
Ubitus|ubitus
iKala|ikala
TXOne Networks|txone-networks
SiliconBox|silicon-box
Lightmatter|lightmatter
Celestial AI|celestial-ai
bolttech|bolttech
Nium|nium
Thunes|thunes
Apptronik|apptronik
Figure AI|figure-b5dc
Sanctuary AI|sanctuary-ai
SiFive|sifive
Chipletz|chipletz
Blue Cheetah Analog Design|blue-cheetah-analog-design
TWAICE|twaice
Zitara|zitara-technologies
Lambda|lambda-labs
Crusoe|crusoe-energy-systems
Together AI|together-1a7e
Hailo|hailo-technologies
StoreDot|storedot
Amity Solutions|amity-solutions
Flash Express|flash-express
Pomelo|pomelo
Freshket|freshket
Huspy|huspy
Stake|stake-properties
Property Finder|propertyfinder
Tabby|tabby
Cover Genius|cover-genius
Qoala|qoala
Lucy Goods|lucy-goods
Qnovia|respira-technologies
JUUL Labs|juul-labs-9719
Trigo|trigo-0711
HIVED|hived
Zedify|zedify
`
    .trim()
    .split("\n")
    .map((entry) => entry.split("|"))
);

const makeCandidate = (label) => {
  const slug = TARGET_SLUGS[label];
  return slug ? { label, slug } : null;
};

const makeCandidates = (labels) => labels.map(makeCandidate).filter(Boolean);

// Full acquirer-to-candidate source data. Candidates without a configured slug
// remain recorded here but are withheld from selection until a slug is supplied.
const TARGET_CANDIDATE_MAPPINGS = `
UltraTech Cement|Carbon Clean|Infra.Market|Hygenco
Mahindra & Mahindra|DeHaat|Euler Motors|Vecmocon
Reliance Industries|Porter|Udaan|Shiprocket
Sun Pharmaceutical Industries|Immuneel Therapeutics|Eyestem|Qure.ai
Bharti Airtel|CloudSEK|Neysa|QNu Labs
MTN Group|Onafriq|Carry1st|Smile Identity
Shoprite Holdings|OmniRetail|Wasoko-MaxAB|Float
AngloGold Ashanti|Earth AI|Fleet Space Technologies|Novamera
Discovery|LifeQ|Quro Medical|AURA
Capitec|Yoco|Float|Lula
Petrobras|Carbon Clean|Twelve|Mombak
Vale|Boston Metal|Allonnia|Fleet Space Technologies
Itaú Unibanco|Celcoin|Creditas|Nomad
Nu Holdings|Creditas|Clara|Nomad
COPEL|Delfos Energy|Lemon Energia|Solfácil
Trip.com Group|Klook|KKday|GetYourGuide
Meituan|Neolix|Pudu Robotics|Keenon Robotics
BYD|DeepRoute.ai|Momenta|Neolix
JD.com|Hai Robotics|Neolix|Quicktron
Ping An Insurance Group|The CareVoice|MediTrust Health|Insgeek
Samsung Electronics|Rebellions|DEEPX|FuriosaAI
Hyundai Motor|DEEPX|RideFlux|bitsensing
Naver|Twelve Labs|Upstage|Wrtn Technologies
Hanwha Aerospace|Perigee Aerospace|Nearthlab|PABLO AIR
SK Hynix|Avicena|Ayar Labs|FuriosaAI
ICICI Bank|M2P Fintech|Signzy|Perfios
Bajaj Finance|Fibe|Jupiter|FinBox
NHPC|Fourth Partner Energy|Aerem|AmpereHour Energy
Kotak Mahindra Bank|Open|FinBox|Jupiter
NTPC|Hygenco|Newtrace|AmpereHour Energy
Eternal|Rebel Foods|Curefoods|Porter
HDFC Bank|Perfios|M2P Fintech|Signzy
State Bank of India|Cashfree Payments|Open|Signzy
ITC|The Whole Truth|Slurrp Farm|Bombay Shaving Company
Tata Motors|Euler Motors|Vecmocon|Exponent Energy
Axis Bank|Open|FinBox|Cashfree Payments
Infosys|Whatfix|Uniphore|Yellow.ai
Tata Consultancy Services|Darwinbox|Whatfix|Yellow.ai
Hindustan Unilever|Pilgrim|Foxtale|The Whole Truth
Power Grid Corporation of India|AmpereHour Energy|EdgeGrid|Kazam
Maruti Suzuki India|Exponent Energy|Vecmocon|Kazam
Sanlam|Naked Insurance|Pineapple|LifeQ
Standard Bank Group|Stitch|Yoco|Omnisient
Gold Fields|Fleet Space Technologies|Plotlogic|Earth AI
Harmony Gold Mining|Novamera|Plotlogic|Fleet Space Technologies
Nedbank Group|Stitch|Smile Identity|Omnisient
Valterra Platinum|Plotlogic|VerAI Discoveries|KoBold Metals
Impala Platinum|Plotlogic|Fleet Space Technologies|VerAI Discoveries
Absa Group|Peach Payments|Lula|Float
FirstRand|Yoco|Omnisient|Peach Payments
Naspers|Carry1st|Tyme Group|OmniRetail
Serena Energia|Lemon Energia|Delfos Energy|Solfácil
Auren Energia|Solfácil|Lemon Energia|Delfos Energy
Companhia Energetica de Minas Gerais (CEMIG)|Solfácil|Lemon Energia|Delfos Energy
ENGIE Brasil Energia|Lemon Energia|Delfos Energy|Solfácil
People's Insurance Co. (Group) of China|The CareVoice|MediTrust Health|Insgeek
Kuaishou Technology|Soul App|Xiaohongshu (RedNote)|MiniMax
Vipshop Holdings|Dewu / Poizon|Xiaohongshu (RedNote)|Cider
China Merchants Bank|XTransfer|Fenbeitong|Qupital
Yum China Holdings|Tastien|M Stand|HeyTea
Xiaomi|DeepRoute.ai|Rokid|Pudu Robotics
PetroChina|Hithium|Envision Energy|Carbon Clean
NetEase|Game Science|Lilith Games|Paper Games
China Construction Bank|XTransfer|Fenbeitong|Qupital
BeiGene / BeOne Medicines|MediLink Therapeutics|Immorna|Accutar Biotechnology
Baidu|DeepRoute.ai|StepFun|MiniMax
Agricultural Bank of China|XTransfer|Fenbeitong|Qupital
PDD Holdings|Cider|Shoplazza|XTransfer
Alibaba Group Holding|Moonshot AI|XTransfer|Pudu Robotics
China Three Gorges Renewables|Hithium|Sigenergy|Envision Energy
China Yangtze Power|Hithium|Sigenergy|Envision Energy
Kweichow Moutai|Jiangxiaobai|Genki Forest|HeyTea
China Shenhua Energy|TAGE IDRIVER|Hithium|Envision Energy
Kingsoft|SiliconFlow|StepFun|ModelBest
Industrial and Commercial Bank of China|XTransfer|Fenbeitong|Qupital
Tencent Holdings|Game Science|MiniMax|Soul App
Bank of China|XTransfer|Airwallex|Qupital
Kia|RideFlux|bitsensing|Seoul Robotics
KB Financial Group|Korea Credit Data|Viva Republica / Toss|Qraft Technologies
Celltrion|Galux|Standigm|Sovargen
Thales|Helsing|Exotrail|Unseenlabs
Société Générale|Qonto|Swan|Pennylane
Legrand|KODE Labs|Sensorberg|enVerid Systems
Orange|Sekoia.io|GitGuardian|Actility
SAP|Celonis|Parloa|Dataiku
Bayer|Owkin|Insilico Medicine|Generate Biomedicines
Heidelberg Materials|Carbon Clean|Sublime Systems|Brimstone
Commerzbank|Trade Republic|Raisin|Scalable Capital
OTP Bank|SEON|Barion|PastPay
Bank Central Asia|Xendit|Mekari|Paper.id
Bank Rakyat Indonesia (Persero)|Amartha|AwanTunai|Funding Societies / Modalku
Bank Mandiri (Persero)|Xendit|Mekari|Funding Societies / Modalku
Nippon Telegraph and Telephone|Sakana AI|Preferred Networks|TIER IV
Seven & i Holdings|Telexistence|Mujin|Oishii Farm
Advantest|proteanTecs|EdgeCortix|Rebellions
Chugai Pharmaceutical|Generate Biomedicines|Insilico Medicine|Elix
National Bank Of Kuwait|Tap Payments|Tarabut|NymCard
Kuwait Finance House|Wahed|NymCard|Tarabut
BOUBYAN BANK|Tap Payments|NymCard|Tarabut
Public Bank|Funding Societies|PolicyStreet|CapBay
Malayan Banking|Funding Societies|MoneyMatch|Carsome
Tenaga Nasional|Ditrolic Energy|Plus Xnergy|VFlowTech
CIMB Group Holdings|Funding Societies|PolicyStreet|MoneyMatch
Fomento Economico Mexicano|Jüsto|99minutos|Yalo
Walmart de México V|Jüsto|99minutos|Yalo
GPO Finance Banorte|Clip|Stori|Belvo
Grupo México|KoBold Metals|MineSense|Plotlogic
Powszechna Kasa Oszczednosci Bank|Silent Eight|Autenti|ZEN.COM
Saudi Arabian Oil|Carbon Clean|OxCCU|Hysata
Al Rajhi Bank|Tamara|Lean Technologies|Lendo
The Saudi National Bank|Lean Technologies|Lendo|NymCard
Saudi Arabian Mining|KoBold Metals|Plotlogic|Fleet Space Technologies
Saudi Basic Industries|Carbon Clean|Twelve|Dioxycle
Saudi Telecom|Unifonic|Lucidya|Mozn
Swiss Life Holding|CLARK|Getsafe|dacadoo
Partners Group Holding|Moonfare|iCapital|AssetMetrix
Nestlé|Formo|Remilk|Onego Bio
Chunghwa Telecom|Ubitus|iKala|TXOne Networks
Taiwan Semicon|SiliconBox|Lightmatter|Celestial AI
Cathay Financial Holding|bolttech|Nium|Airwallex
CTBC Financial Holding|Nium|Airwallex|Thunes
Hon Hai Precision Industry|Apptronik|Figure AI|Sanctuary AI
Fubon Financial Holding|bolttech|Nium|Airwallex
United Micro Electronics|SiFive|Chipletz|Blue Cheetah Analog Design
Delta Electronics|VFlowTech|TWAICE|Zitara
Quanta Computer|Lambda|Crusoe|Together AI
Mega Financial Holding|Nium|Thunes|Airwallex
MediaTek|SiFive|EdgeCortix|Hailo
PTT|Hysata|Twelve|StoreDot
Advanced Info Service (Non-Voting DR)|Amity Solutions|iKala|Ubitus
CP ALL|Flash Express|Pomelo|Freshket
Emaar Properties|Huspy|Stake|Property Finder
First Abu Dhabi Bank|Tabby|NymCard|Lean Technologies
Emirates Telecom|Unifonic|Mozn|Lucidya
Prudential|bolttech|Cover Genius|Qoala
Imperial Brands|Lucy Goods|Qnovia|JUUL Labs
Tesco|Trigo|HIVED|Zedify
Rio Tinto|KoBold Metals|MineSense|Plotlogic
`
  .trim()
  .split("\n")
  .map((row) => row.split("|"));

// The selected CompanyMonitor company is the acquirer. Every source mapping is
// retained, while only targets with a known slug are selectable.
const TARGETS_BY_ACQUIRER = Object.fromEntries(
  TARGET_CANDIDATE_MAPPINGS.map(([acquirer, ...candidateLabels]) => [
    normalizeCompanyKey(acquirer),
    makeCandidates(candidateLabels),
  ])
);
const REPORT_OUTPUTS = [
  {
    value: "html",
    label: "Textual analysis",
    detail: "HTML briefing",
    icon: FileText,
  },
  {
    value: "spreadsheet",
    label: "Spreadsheet",
    detail: "Workbook + Excel export",
    icon: Table2,
  },
];

const INITIAL_REPORT_STATE = {
  workbooks: {},
  activeWorkbookId: null,
  activeSheetIndex: 0,
};
const createInitialReportSessionState = () => ({
  reportKindKey: "",
  candidateSlug: "",
  outputType: "",
  selectionStep: "kind",
  reportState: { ...INITIAL_REPORT_STATE },
  isGenerating: false,
  streamError: "",
  htmlResponse: "",
});
const reportSessions = new Map();

const getReportSessionRecord = (sessionKey) => {
  if (!reportSessions.has(sessionKey)) {
    reportSessions.set(sessionKey, {
      state: createInitialReportSessionState(),
      listeners: new Set(),
      socket: null,
      isComplete: false,
      runId: 0,
    });
  }

  return reportSessions.get(sessionKey);
};

const notifyReportSession = (session) => {
  session.listeners.forEach((listener) => listener());
};

const updateReportSession = (sessionKey, update) => {
  const session = getReportSessionRecord(sessionKey);
  session.state = typeof update === "function" ? update(session.state) : update;
  notifyReportSession(session);
};

const subscribeToReportSession = (sessionKey, listener) => {
  const session = getReportSessionRecord(sessionKey);
  session.listeners.add(listener);
  return () => session.listeners.delete(listener);
};

const closeReportSessionSocket = (sessionKey) => {
  const session = getReportSessionRecord(sessionKey);
  const socket = session.socket;
  session.socket = null;
  try {
    socket?.close();
  } catch {
    // Closing a socket that has already ended is safe to ignore.
  }
};

const resetReportSession = (sessionKey) => {
  const session = getReportSessionRecord(sessionKey);
  session.isComplete = true;
  session.runId += 1;
  closeReportSessionSocket(sessionKey);
  session.state = createInitialReportSessionState();
  notifyReportSession(session);
};
const REPORT_STREAM_CONTROL_TYPES = new Set([
  "INFO",
  "MESSAGE_ID",
  "SUGGESTIONS",
  "START",
  "STATUS",
  "PING",
  "PONG",
  "HEARTBEAT",
  "FINISH",
]);
const VIRTUAL_SHEET_ROW_GAP = 4;
const SHEET_REFERENCE_PATTERN =
  /(?:'((?:[^']|'')+)'|([A-Za-z_][A-Za-z0-9_]*))!(\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?)/gi;
const LOCAL_REFERENCE_PATTERN =
  /(^|[^A-Z0-9_])(\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?)(?=$|[^A-Z0-9_])/g;
const CELL_REFERENCE_PART_PATTERN = /(\$?)([A-Z]{1,3})(\$?)(\d+)/gi;

const safeParseMessage = (message) => {
  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message && typeof message === "object" ? message : null;
};

// react-spreadsheet derives the rendered column count from the first row. Report
// workbooks commonly begin with a single-cell title row, so pad every row to the
// widest row before handing the matrix to the grid.
const normalizeSpreadsheetRows = (rows) => {
  const columnCount = Math.max(
    1,
    ...rows.map((row) => (Array.isArray(row) ? row.length : 0))
  );

  return rows.map((row) =>
    Array.from(
      { length: columnCount },
      (_, columnIndex) => (Array.isArray(row) ? row[columnIndex] ?? null : null)
    )
  );
};

const getReportHtmlChunk = (type, message) => {
  const normalizedType = String(type || "").trim().toUpperCase();
  if (
    REPORT_STREAM_CONTROL_TYPES.has(normalizedType) ||
    normalizedType.startsWith("REPORT_WORKBOOK_") ||
    normalizedType === "ERROR" ||
    normalizedType === "REPORT_ERROR" ||
    typeof message !== "string"
  ) {
    return "";
  }

  return message;
};

const isNumeric = (value) =>
  typeof value === "number" && Number.isFinite(value);

const getCellFormula = (cell) => {
  if (typeof cell?.formula === "string" && cell.formula.startsWith("=")) {
    return cell.formula;
  }
  return typeof cell?.value === "string" && cell.value.startsWith("=")
    ? cell.value
    : null;
};

const getCellDisplayValue = (cell) =>
  cell?.display_value ?? cell?.computed_value ?? cell?.value;

const normalizeSheetName = (sheetName) =>
  String(sheetName || "")
    .replace(/''/g, "'")
    .trim()
    .toLowerCase();

const shiftCellReference = (reference, rowOffset) =>
  reference.replace(
    CELL_REFERENCE_PART_PATTERN,
    (_, columnAbsolute, column, rowAbsolute, row) =>
      `${columnAbsolute}${column.toUpperCase()}${rowAbsolute}${Number(row) + rowOffset}`
  );

const rewriteFormulaSegment = (segment, currentSheetOffset, sheetOffsets) => {
  const qualifiedReferences = [];
  const withVirtualReferences = segment.replace(
    SHEET_REFERENCE_PATTERN,
    (match, quotedSheetName, plainSheetName, reference) => {
      const sheetOffset = sheetOffsets.get(
        normalizeSheetName(quotedSheetName || plainSheetName)
      );
      if (typeof sheetOffset !== "number") return match;

      const placeholder = `__reportRef${qualifiedReferences.length}__`;
      qualifiedReferences.push([placeholder, shiftCellReference(reference, sheetOffset)]);
      return placeholder;
    }
  );
  const withLocalReferences = withVirtualReferences.replace(
    LOCAL_REFERENCE_PATTERN,
    (_, prefix, reference) => `${prefix}${shiftCellReference(reference, currentSheetOffset)}`
  );

  return qualifiedReferences.reduce(
    (rewrittenFormula, [placeholder, reference]) =>
      rewrittenFormula.replace(placeholder, reference),
    withLocalReferences
  );
};

const rewriteFormulaForVirtualSheet = (formula, currentSheetOffset, sheetOffsets) =>
  formula
    .split(/("(?:""|[^"])*")/g)
    .map((segment, index) =>
      index % 2 === 1
        ? segment
        : rewriteFormulaSegment(segment, currentSheetOffset, sheetOffsets)
    )
    .join("");

const isUsableCalculatedValue = (formula, value) =>
  value !== null &&
  typeof value !== "undefined" &&
  value !== formula &&
  !(typeof value === "string" && (value.startsWith("=") || value.startsWith("#")));

const calculateWorkbookDisplaySheets = (workbook) => {
  const sheetCount = Math.max(workbook.expectedSheetNames.length, workbook.sheets.length);
  let nextRowOffset = 0;
  const descriptors = Array.from({ length: sheetCount }, (_, sheetIndex) => {
    const sheet = workbook.sheets[sheetIndex];
    const rows = sheet?.rows || [];
    const descriptor = {
      sheetIndex,
      name: sheet?.name || workbook.expectedSheetNames[sheetIndex] || `Sheet ${sheetIndex + 1}`,
      rows,
      rowOffset: nextRowOffset,
    };
    nextRowOffset += Math.max(rows.length, 1) + VIRTUAL_SHEET_ROW_GAP;
    return descriptor;
  });
  const sheetOffsets = new Map(
    descriptors.map((descriptor) => [normalizeSheetName(descriptor.name), descriptor.rowOffset])
  );
  const virtualColumnCount = Math.max(
    1,
    ...descriptors.flatMap((descriptor) => descriptor.rows.map((row) => row.length))
  );
  const virtualData = Array.from(
    { length: nextRowOffset },
    () => Array(virtualColumnCount)
  );

  descriptors.forEach((descriptor) => {
    descriptor.rows.forEach((row, rowIndex) => {
      row.forEach((cell, columnIndex) => {
        if (!cell) return;
        const formula = getCellFormula(cell);
        virtualData[descriptor.rowOffset + rowIndex][columnIndex] = formula
          ? {
              ...cell,
              value: rewriteFormulaForVirtualSheet(
                formula,
                descriptor.rowOffset,
                sheetOffsets
              ),
            }
          : cell;
      });
    });
  });

  try {
    const evaluatedVirtualData = new Model(createFormulaParser, virtualData).evaluatedData;

    return workbook.sheets.map((sheet, sheetIndex) => {
      if (!sheet) return sheet;
      const descriptor = descriptors[sheetIndex];
      return {
        ...sheet,
        rows: sheet.rows.map((row, rowIndex) =>
          row.map((cell, columnIndex) => {
            const formula = getCellFormula(cell);
            const calculatedValue =
              evaluatedVirtualData[descriptor.rowOffset + rowIndex]?.[columnIndex]?.value;

            if (
              !formula ||
              cell?.display_value !== undefined ||
              cell?.computed_value !== undefined ||
              !isUsableCalculatedValue(formula, calculatedValue)
            ) {
              return cell;
            }

            return { ...cell, computed_value: calculatedValue };
          })
        ),
      };
    });
  } catch {
    return workbook.sheets;
  }
};

const EXCEL_NUMBER_FORMATS = {
  money: "₹#,##0.00",
  percentage: "0.00%",
  multiple: '0.00"x"',
  per_share: '₹#,##0.00" / share"',
  date: "dd-mmm-yyyy",
};

const getExcelSafeSheetName = (sheetName, sheetIndex, usedSheetNames) => {
  const fallbackName = `Sheet ${sheetIndex + 1}`;
  const baseName = String(sheetName || fallbackName)
    .replace(/[\\/?*:]/g, " ")
    .replaceAll("[", " ")
    .replaceAll("]", " ")
    .trim()
    .slice(0, 31) || fallbackName;
  let candidate = baseName;
  let duplicateNumber = 2;

  while (usedSheetNames.has(candidate.toLowerCase())) {
    const suffix = ` (${duplicateNumber})`;
    candidate = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    duplicateNumber += 1;
  }

  usedSheetNames.add(candidate.toLowerCase());
  return candidate;
};

const quoteExcelSheetName = (sheetName) => `'${String(sheetName).replace(/'/g, "''")}'`;

const rewriteExcelFormulaSheetNames = (formula, sheetNameMap) =>
  formula.replace(
    SHEET_REFERENCE_PATTERN,
    (match, quotedSheetName, plainSheetName, reference) => {
      const exportSheetName = sheetNameMap.get(
        normalizeSheetName(quotedSheetName || plainSheetName)
      );
      return exportSheetName
        ? `${quoteExcelSheetName(exportSheetName)}!${reference}`
        : match;
    }
  );

const getExcelCell = (cell, sheetNameMap) => {
  const value = cell?.value;
  const formula = getCellFormula(cell);
  if ((value === null || typeof value === "undefined") && !formula) return null;

  const excelCell = {};
  if (formula) {
    const displayValue = getCellDisplayValue(cell);
    const cachedValue =
      displayValue === null ||
      typeof displayValue === "undefined" ||
      (typeof displayValue === "string" && displayValue.startsWith("="))
        ? 0
        : displayValue;
    // SheetJS requires a cached value for formula cells to be included in the XLSX.
    // A calculated display value, when streamed, keeps non-recalculating viewers useful.
    excelCell.f = rewriteExcelFormulaSheetNames(formula, sheetNameMap).slice(1);
    excelCell.t = typeof cachedValue === "boolean" ? "b" : typeof cachedValue === "number" ? "n" : "s";
    excelCell.v = cachedValue;
  } else {
    excelCell.v = value;
    excelCell.t = typeof value === "number" ? "n" : typeof value === "boolean" ? "b" : "s";
  }

  if (EXCEL_NUMBER_FORMATS[cell?.format]) {
    excelCell.z = EXCEL_NUMBER_FORMATS[cell.format];
  }

  return excelCell;
};

const makeWorkbookFilename = (reportKindKey, candidateSlug) =>
  reportKindKey === "credit"
    ? "public-company-credit-screening.xlsx"
    : reportKindKey === "geographical-expansion"
      ? "geographical-expansion.xlsx"
      : `ma-accretion-dilution-${candidateSlug || "report"}.xlsx`;

const formatCellValue = (value, format, currency) => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "string" && value.startsWith("=")) return value;
  if (format === "date") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    }
  }
  if (!isNumeric(value)) return String(value);

  const numberFormat = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  switch (format) {
    case "money":
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    case "percentage":
      return new Intl.NumberFormat("en-IN", {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    case "multiple":
      return `${numberFormat.format(value)}x`;
    case "per_share":
      return `${new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value)} / share`;
    default:
      return numberFormat.format(value);
  }
};

const WorkbookDataViewer = ({ cell, evaluatedCell, currency }) => {
  const format = cell?.format || "plain";
  const formula = getCellFormula(cell);
  const streamedDisplayValue = cell?.display_value ?? cell?.computed_value;
  const spreadsheetDisplayValue = evaluatedCell?.value;
  const canUseSpreadsheetValue =
    formula &&
    spreadsheetDisplayValue !== null &&
    typeof spreadsheetDisplayValue !== "undefined" &&
    spreadsheetDisplayValue !== formula &&
    !(typeof spreadsheetDisplayValue === "string" && spreadsheetDisplayValue.startsWith("#"));
  const displayValue =
    streamedDisplayValue ??
    (canUseSpreadsheetValue ? spreadsheetDisplayValue : cell?.value);
  const isShowingFormula = formula && displayValue === formula;

  return (
    <span
      className={`report-workbook-cell report-workbook-cell--${format}${
        isShowingFormula ? " report-workbook-cell--formula" : ""
      }`}
      title={formula ? `Formula: ${formula}` : undefined}
    >
      {formatCellValue(displayValue, format, currency)}
    </span>
  );
};

const WorkbookViewer = ({
  workbook,
  activeSheetIndex,
  onSelectSheet,
  onSheetChange,
  onDownload,
}) => {
  const receivedSheetCount = workbook.sheets.filter(Boolean).length;
  const expectedSheetCount = Math.max(
    workbook.expectedSheetNames.length,
    workbook.sheets.length
  );
  const workbookProgress = expectedSheetCount
    ? Math.round((receivedSheetCount / expectedSheetCount) * 100)
    : 0;
  const isCreditWorkbook = workbook.reportKindKey === "credit";
  const nextWorksheetNumber = Math.min(
    receivedSheetCount + 1,
    Math.max(expectedSheetCount, 1)
  );
  const canDownload = workbook.isComplete || !isCreditWorkbook;
  const availableSheetIndices = useMemo(
    () =>
      workbook.expectedSheetNames.map((expectedName, index) => ({
        index,
        name: workbook.sheets[index]?.name || expectedName,
        isAvailable: Boolean(workbook.sheets[index]),
      })),
    [workbook.expectedSheetNames, workbook.sheets]
  );
  const displaySheets = useMemo(() => calculateWorkbookDisplaySheets(workbook), [workbook]);
  const activeSheet = displaySheets[activeSheetIndex];
  const dataViewer = useCallback(
    (props) => <WorkbookDataViewer {...props} currency={workbook.currency} />,
    [workbook.currency]
  );

  return (
    <div className="report-workbook-viewer">
      <div className="report-workbook-toolbar">
        <div className="report-workbook-title-block">
          <div className="report-workbook-eyebrow">
            <Table2 size={13} aria-hidden="true" /> Workbook
          </div>
          <h3 className="report-workbook-title">{workbook.reportKindLabel}</h3>
          <p className="report-workbook-meta">
            {isCreditWorkbook && !workbook.isComplete
              ? `Generating worksheet ${nextWorksheetNumber} of ${expectedSheetCount || 1}…`
              : `${receivedSheetCount} of ${expectedSheetCount} sheets received`}
          </p>
        </div>
        <div className="report-workbook-toolbar-actions">
          {canDownload && (
            <Button
              size="sm"
              className="report-workbook-download"
              onClick={onDownload}
              disabled={!workbook.sheets.some(Boolean)}
              title={
                workbook.isComplete
                  ? "Download the completed Excel workbook"
                  : "Download the sheets received so far"
              }
            >
              Download spreadsheet
              <Download size={14} aria-hidden="true" />
            </Button>
          )}
          <div
            className={`report-workbook-status ${
              workbook.isComplete ? "is-complete" : "is-loading"
            }`}
          >
            <span className="report-workbook-status-dot" />
            {workbook.isComplete ? "Workbook complete" : "Building workbook"}
          </div>
        </div>
        <div
          className="report-workbook-progress"
          role="progressbar"
          aria-label="Workbook generation progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={workbookProgress}
        >
          <span style={{ width: `${workbookProgress}%` }} />
        </div>
      </div>

      <div className="report-workbook-tabs" role="tablist" aria-label="Workbook sheets">
        {availableSheetIndices.map((sheet) => (
          <button
            key={`${workbook.workbookId}-${sheet.index}`}
            type="button"
            className={`report-workbook-tab ${
              activeSheetIndex === sheet.index ? "is-active" : ""
            }`}
            onClick={() => onSelectSheet(sheet.index)}
            role="tab"
            aria-selected={activeSheetIndex === sheet.index}
          >
            {sheet.name}
            {!sheet.isAvailable && <span className="report-workbook-tab-pulse" />}
          </button>
        ))}
      </div>

      <div className="report-workbook-grid-wrap">
        {activeSheet ? (
          <Spreadsheet
            key={`${workbook.reportRunId}-${workbook.workbookId}-${activeSheetIndex}-${activeSheet.name}-${workbook.sheetRenderVersion}`}
            className="report-workbook-grid"
            data={activeSheet.rows}
            DataViewer={dataViewer}
            onChange={(rows) => onSheetChange(activeSheetIndex, rows)}
          />
        ) : (
          <div className="report-workbook-awaiting-sheet">
            <Spinner animation="border" size="sm" />
            <span>
              Waiting for {availableSheetIndices[activeSheetIndex]?.name || "sheet"}…
            </span>
          </div>
        )}
      </div>

      {!workbook.isComplete && (
        <div className="report-workbook-loading-note">
          <Spinner animation="border" size="sm" />
          <span>Sheets appear as they arrive. You can review or download progress at any time.</span>
        </div>
      )}
    </div>
  );
};

const ReportAnalysis = ({
  isDark: isDarkProp,
  acquirerCompany,
  acquirerTicker,
  dashboardName,
  companyId,
  HtmlRenderer,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const reportSessionKey = [
    dashboardName || "",
    companyId || "",
    acquirerCompany || "",
    acquirerTicker || "",
  ].join("|");
  const reportSession = useSyncExternalStore(
    useCallback(
      (listener) => subscribeToReportSession(reportSessionKey, listener),
      [reportSessionKey]
    ),
    useCallback(
      () => getReportSessionRecord(reportSessionKey).state,
      [reportSessionKey]
    )
  );
  const previousReportSessionKeyRef = useRef(reportSessionKey);
  const updateSessionState = useCallback(
    (update) => updateReportSession(reportSessionKey, update),
    [reportSessionKey]
  );
  const setSessionValue = useCallback(
    (key, value) => {
      updateSessionState((previous) => ({
        ...previous,
        [key]: typeof value === "function" ? value(previous[key]) : value,
      }));
    },
    [updateSessionState]
  );
  const setReportKindKey = (value) => setSessionValue("reportKindKey", value);
  const setCandidateSlug = (value) => setSessionValue("candidateSlug", value);
  const setOutputType = (value) => setSessionValue("outputType", value);
  const setSelectionStep = (value) => setSessionValue("selectionStep", value);
  const setReportState = useCallback(
    (value) => setSessionValue("reportState", value),
    [setSessionValue]
  );
  const setIsGenerating = (value) => setSessionValue("isGenerating", value);
  const setStreamError = (value) => setSessionValue("streamError", value);
  const setHtmlResponse = (value) => setSessionValue("htmlResponse", value);
  const {
    reportKindKey,
    candidateSlug,
    outputType,
    selectionStep,
    reportState,
    isGenerating,
    streamError,
    htmlResponse,
  } = reportSession;

  const activeWorkbook = reportState.activeWorkbookId
    ? reportState.workbooks[reportState.activeWorkbookId]
    : null;
  const reportCandidates = useMemo(
    () => TARGETS_BY_ACQUIRER[normalizeCompanyKey(acquirerCompany)] || [],
    [acquirerCompany]
  );
  const hasTargets = reportCandidates.length > 0;
  const selectedReportKind = REPORT_KINDS.find((reportKind) => reportKind.value === reportKindKey);
  const isMAndAReport = selectedReportKind?.requiresTarget;
  const isCreditReport = selectedReportKind?.value === "credit";
  const selectedCandidate = reportCandidates.find(
    (candidate) => candidate.slug === candidateSlug
  );
  const selectedOutput = REPORT_OUTPUTS.find((output) => output.value === outputType);
  const hasReportContent = Boolean(activeWorkbook || htmlResponse || isGenerating || streamError);

  const closeReportSocket = useCallback(() => {
    closeReportSessionSocket(reportSessionKey);
  }, [reportSessionKey]);

  useEffect(() => {
    const previousSessionKey = previousReportSessionKeyRef.current;
    if (previousSessionKey !== reportSessionKey) {
      resetReportSession(previousSessionKey);
      previousReportSessionKeyRef.current = reportSessionKey;
    }
  }, [reportSessionKey]);

  const clearReport = useCallback(() => {
    resetReportSession(reportSessionKey);
  }, [reportSessionKey]);

  const updateWorkbook = useCallback((workbookId, updater) => {
    setReportState((previous) => {
      const currentWorkbook = previous.workbooks[workbookId];
      if (!currentWorkbook) return previous;

      return {
        ...previous,
        workbooks: {
          ...previous.workbooks,
          [workbookId]: updater(currentWorkbook),
        },
      };
    });
  }, [setReportState]);

  const handleWorkbookEvent = useCallback(
    (type, payload, reportRunId) => {
      if (!payload?.workbook_id) return false;
      if (reportRunId !== getReportSessionRecord(reportSessionKey).runId) return true;

      if (type === "REPORT_WORKBOOK_START") {
        const expectedSheetNames = Array.isArray(payload.expected_sheet_names)
          ? payload.expected_sheet_names
          : [];
        const workbookId = payload.workbook_id;

        setReportState((previous) => ({
          ...previous,
          activeWorkbookId: workbookId,
          activeSheetIndex: 0,
          workbooks: {
            ...previous.workbooks,
            [workbookId]: {
              workbookId,
              reportRunId,
              sheetRenderVersion: 0,
              currency: payload.currency || "INR",
              expectedSheetNames,
              reportKindKey: selectedReportKind?.value || "ma",
              reportKindLabel: selectedReportKind?.label || REPORT_KIND,
              renderer: payload.renderer,
              sheets: Array(expectedSheetNames.length),
              isComplete: false,
            },
          },
        }));
        return true;
      }

      if (type === "REPORT_WORKBOOK_SHEET") {
        const { workbook_id: workbookId, sheet_index: sheetIndex, sheet } = payload;
        const rows = Array.isArray(sheet?.rows)
          ? sheet.rows
          : Array.isArray(sheet?.data)
            ? sheet.data
            : null;
        if (!Number.isInteger(sheetIndex) || !sheet?.name || !rows) {
          return true;
        }

        updateWorkbook(workbookId, (workbook) => {
          if (workbook.reportRunId !== reportRunId) return workbook;
          const normalizedRows = normalizeSpreadsheetRows(rows);
          const sheets = [...workbook.sheets];
          sheets[sheetIndex] = {
            ...sheet,
            rows:
              workbook.reportKindKey === "credit"
                ? normalizedRows.map((row) =>
                    row.map((cell) => (cell ? { ...cell, readOnly: true } : cell))
                  )
                : normalizedRows,
          };
          return {
            ...workbook,
            sheets,
            sheetRenderVersion: workbook.sheetRenderVersion + 1,
          };
        });
        setReportState((previous) => ({
          ...previous,
          activeSheetIndex:
            previous.activeWorkbookId === workbookId &&
            previous.workbooks[workbookId]?.reportRunId === reportRunId
              ? sheetIndex
              : previous.activeSheetIndex,
        }));
        return true;
      }

      if (type === "REPORT_WORKBOOK_COMPLETE") {
        updateWorkbook(payload.workbook_id, (workbook) =>
          workbook.reportRunId === reportRunId
            ? { ...workbook, isComplete: true, sheetCount: payload.sheet_count }
            : workbook
        );
        return true;
      }

      return false;
    },
    [reportSessionKey, selectedReportKind, setReportState, updateWorkbook]
  );

  const handleSheetChange = useCallback(
    (sheetIndex, rows) => {
      if (!reportState.activeWorkbookId) return;
      updateWorkbook(reportState.activeWorkbookId, (workbook) => {
        const currentSheet = workbook.sheets[sheetIndex];
        if (!currentSheet) return workbook;
        const sheets = [...workbook.sheets];
        sheets[sheetIndex] = { ...currentSheet, rows };
        return { ...workbook, sheets };
      });
    },
    [reportState.activeWorkbookId, updateWorkbook]
  );

  const downloadWorkbook = useCallback(() => {
    if (!activeWorkbook?.sheets.some(Boolean)) return;

    const xlsxWorkbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    const exportSheetNames = activeWorkbook.sheets.map((sheet, sheetIndex) =>
      getExcelSafeSheetName(sheet?.name, sheetIndex, usedSheetNames)
    );
    const sheetNameMap = new Map(
      activeWorkbook.sheets.map((sheet, sheetIndex) => [
        normalizeSheetName(sheet?.name || `Sheet ${sheetIndex + 1}`),
        exportSheetNames[sheetIndex],
      ])
    );

    activeWorkbook.sheets.forEach((sheet, sheetIndex) => {
      if (!sheet?.rows?.length) return;

      const worksheet = {};
      let finalColumnIndex = 0;

      sheet.rows.forEach((row, rowIndex) => {
        row.forEach((cell, columnIndex) => {
          finalColumnIndex = Math.max(finalColumnIndex, columnIndex);
          const excelCell = getExcelCell(cell, sheetNameMap);
          if (!excelCell) return;
          worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })] = excelCell;
        });
      });

      worksheet["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: sheet.rows.length - 1, c: finalColumnIndex },
      });
      XLSX.utils.book_append_sheet(
        xlsxWorkbook,
        worksheet,
        exportSheetNames[sheetIndex]
      );
    });

    if (!xlsxWorkbook.SheetNames.length) return;
    XLSX.writeFile(
      xlsxWorkbook,
      makeWorkbookFilename(activeWorkbook.reportKindKey, candidateSlug),
      {
        compression: true,
      }
    );
  }, [activeWorkbook, candidateSlug]);

  const generateReport = async () => {
    if (
      isGenerating ||
      !selectedReportKind ||
      (selectedReportKind.requiresTarget && !candidateSlug) ||
      !outputType ||
      !acquirerCompany ||
      !acquirerTicker
    ) {
      return;
    }

    closeReportSocket();
    const session = getReportSessionRecord(reportSessionKey);
    session.isComplete = false;
    const reportRunId = session.runId + 1;
    session.runId = reportRunId;
    setIsGenerating(true);
    setStreamError("");
    setHtmlResponse("");
    if (outputType === "spreadsheet") {
      setReportState({ ...INITIAL_REPORT_STATE });
    }

    // Preserve the existing M&A and credit contracts. Geographical Expansion
    // intentionally only needs the selected company's ticker and context.
    const chatId = isMAndAReport
      ? `REPORTGPT-${getUserId() || "anonymous"}-${Date.now()}`
      : `ReportGPT-${crypto.randomUUID()}`;
    const reportContext = {
      dashboard_name: dashboardName || "",
      company_id: companyId || "",
    };
    const requestBody = isMAndAReport
      ? {
          origin: "ReportGPT",
          report_kind: REPORT_KIND,
          output_type: outputType,
          company: acquirerCompany,
          target_company_slug: candidateSlug,
          acquirer_ticker: acquirerTicker,
          chat_id: chatId,
          ...reportContext,
        }
      : isCreditReport
        ? {
            query: CREDIT_REPORT_QUERY,
            company: acquirerCompany,
            ticker: acquirerTicker,
            origin: "REPORTGPT",
            report_kind: CREDIT_REPORT_KIND,
            output_type: outputType,
            chat_id: chatId,
            sender: getUserId(),
            sender_display_name: "USER",
            ...reportContext,
          }
        : {
            origin: "REPORTGPT",
            ticker: acquirerTicker,
            report_kind: GEOGRAPHICAL_EXPANSION_REPORT_KIND,
            output_type: outputType,
            chat_id: chatId,
            ...reportContext,
          };

    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(chatId, getUserId());
      const ws = new WebSocket(wsUrl);
      if (reportRunId !== getReportSessionRecord(reportSessionKey).runId) {
        ws.close();
        return;
      }
      session.socket = ws;

      ws.onopen = () => ws.send(JSON.stringify(requestBody));
      ws.onmessage = (event) => {
        if (reportRunId !== getReportSessionRecord(reportSessionKey).runId) return;
        const data = safeParseMessage(event.data);
        if (!data) {
          // Keepalives and non-JSON transport frames are not report failures.
          return;
        }

        const eventType = String(data.type || "").trim().toUpperCase();
        if (eventType === "FINISH") {
          getReportSessionRecord(reportSessionKey).isComplete = true;
          setIsGenerating(false);
          ws.close();
          return;
        }

        if (eventType === "ERROR" || eventType === "REPORT_ERROR") {
          const message =
            typeof data.message === "string" && data.message.trim()
              ? data.message
              : "The report could not be generated. Please try again.";
          setStreamError(message);
          return;
        }

        try {
          const workbookPayload = safeParseMessage(data.message);
          const isWorkbookEvent = handleWorkbookEvent(
            data.type,
            workbookPayload,
            reportRunId
          );
          const htmlChunk = getReportHtmlChunk(data.type, data.message);

          if (!isWorkbookEvent && outputType === "html" && htmlChunk) {
            setHtmlResponse((previous) => previous + htmlChunk);
          }
        } catch (error) {
          // A malformed optional frame must not mark a successfully streaming
          // report as failed. Websocket close/ERROR events still surface real failures.
          console.warn("Ignoring unreadable ReportGPT stream frame:", error);
        }
      };

      ws.onerror = () => {
        if (reportRunId !== getReportSessionRecord(reportSessionKey).runId) return;
        setStreamError("The report connection could not be established. Please try again.");
      };

      ws.onclose = () => {
        const activeSession = getReportSessionRecord(reportSessionKey);
        const isActiveSocket = activeSession.socket === ws;
        if (isActiveSocket && !activeSession.isComplete) {
          setStreamError((previous) =>
            previous || "The report stream ended before it finished. Please try again."
          );
        }
        if (isActiveSocket) {
          setIsGenerating(false);
          activeSession.socket = null;
        }
      };
    } catch (error) {
      console.error("Unable to start ReportGPT websocket:", error);
      setStreamError("Unable to start the report. Please check your connection and try again.");
      setIsGenerating(false);
    }
  };

  const selectReportKind = (nextReportKindKey) => {
    const nextReportKind = REPORT_KINDS.find(
      (reportKind) => reportKind.value === nextReportKindKey
    );
    if (!nextReportKind) return;

    setReportKindKey(nextReportKindKey);
    setCandidateSlug("");
    setOutputType("");
    setSelectionStep(
      nextReportKind.requiresTarget
        ? hasTargets
          ? "candidate"
          : "no-target"
        : "format"
    );
  };

  const selectCandidate = (slug) => {
    setCandidateSlug(slug);
    setOutputType("");
    setSelectionStep("format");
  };

  const selectOutputType = (nextOutputType) => {
    setOutputType(nextOutputType);
    setSelectionStep("ready");
  };

  const reportPanelClass = isDark
    ? "report-analysis report-analysis--dark"
    : "report-analysis";

  return (
    <div className={reportPanelClass}>
      {selectionStep === "kind" && (
        <section className="report-analysis-section report-analysis-selection-card">
          <div className="report-analysis-step-heading">
            <span className="report-analysis-step-number">01</span>
            <div>
              <span className="report-analysis-label">Select report type</span>
              <p>Choose the analysis you want to run for {acquirerCompany}.</p>
            </div>
          </div>
          <div className="report-analysis-options" role="radiogroup" aria-label="Report type">
            {REPORT_KINDS.map((reportKind) => {
              const ReportKindIcon = reportKind.icon;
              return (
                <button
                  key={reportKind.value}
                  type="button"
                  className={`report-analysis-option ${
                    reportKindKey === reportKind.value ? "is-selected" : ""
                  } report-analysis-option--choice`}
                  onClick={() => selectReportKind(reportKind.value)}
                  role="radio"
                  aria-checked={reportKindKey === reportKind.value}
                  disabled={isGenerating}
                >
                  <ReportKindIcon size={17} aria-hidden="true" />
                  <span className="report-analysis-option-copy">
                    <strong>{reportKind.label}</strong>
                    <small>{reportKind.detail}</small>
                  </span>
                  <ArrowRight className="report-analysis-option-arrow" size={16} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {selectionStep === "no-target" && (
        <section className="report-analysis-empty-state">
          <span className="report-analysis-empty-icon">
            <Building2 size={20} aria-hidden="true" />
          </span>
          <div>
            <strong>No target available</strong>
            <p>
              We do not have an M&amp;A target shortlist for {acquirerCompany || "this company"}.
            </p>
          </div>
          <Button
            size="sm"
            className="report-analysis-change"
            onClick={() => setSelectionStep("kind")}
            disabled={isGenerating}
          >
            <RefreshCw size={13} aria-hidden="true" />
            Change type
          </Button>
        </section>
      )}

      {selectionStep === "candidate" && (
        <section className="report-analysis-section report-analysis-selection-card">
          <div className="report-analysis-step-heading">
            <span className="report-analysis-step-number">02</span>
            <div>
              <span className="report-analysis-label">Select a target</span>
              <p>Choose the company to assess against {acquirerCompany}.</p>
            </div>
          </div>
          <div className="report-analysis-options" role="radiogroup" aria-label="M&A candidate">
            {reportCandidates.map((candidate) => (
              <button
                key={candidate.slug}
                type="button"
                className={`report-analysis-option ${
                  candidateSlug === candidate.slug ? "is-selected" : ""
                } report-analysis-option--choice`}
                onClick={() => selectCandidate(candidate.slug)}
                role="radio"
                aria-checked={candidateSlug === candidate.slug}
                disabled={isGenerating}
              >
                <Building2 size={17} aria-hidden="true" />
                <span className="report-analysis-option-copy">
                  <strong>{candidate.label}</strong>
                  {candidate.detail && <small>{candidate.detail}</small>}
                </span>
                <ArrowRight className="report-analysis-option-arrow" size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      )}

      {selectionStep === "format" && (
        <section className="report-analysis-section report-analysis-selection-card">
          <div className="report-analysis-step-heading">
            <span className="report-analysis-step-number">
              {isMAndAReport ? "03" : "02"}
            </span>
            <div>
              <span className="report-analysis-label">Choose delivery</span>
              <p>Pick a narrative briefing or an interactive workbook.</p>
            </div>
          </div>
          <div className="report-analysis-options" role="radiogroup" aria-label="Output format">
            {REPORT_OUTPUTS.map((output) => {
              const OutputIcon = output.icon;
              return (
                <button
                  key={output.value}
                  type="button"
                  className={`report-analysis-option ${
                    outputType === output.value ? "is-selected" : ""
                  } report-analysis-option--choice`}
                  onClick={() => selectOutputType(output.value)}
                  role="radio"
                  aria-checked={outputType === output.value}
                  disabled={isGenerating}
                >
                  <OutputIcon size={17} aria-hidden="true" />
                  <span className="report-analysis-option-copy">
                    <strong>{output.label}</strong>
                    <small>{output.detail}</small>
                  </span>
                  <ArrowRight className="report-analysis-option-arrow" size={16} aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {selectionStep === "ready" && (
        <>
          <div className="report-analysis-selection-summary">
            <div className="report-analysis-selection-ready">
              <span className="report-analysis-ready-label">
                <CheckCircle2 size={14} aria-hidden="true" /> Ready to generate
              </span>
              <strong>
                {selectedReportKind?.label}
                {isMAndAReport && (
                  <>
                    <span className="report-analysis-selection-divider">•</span>
                    {selectedCandidate?.label}
                  </>
                )}
                <span className="report-analysis-selection-divider">•</span>
                {selectedOutput?.label}
              </strong>
            </div>
            <Button
              size="sm"
              className="report-analysis-change"
              onClick={() => setSelectionStep("kind")}
              disabled={isGenerating}
            >
              <RefreshCw size={13} aria-hidden="true" />
              Change
            </Button>
          </div>

          <div className="report-analysis-actions">
            {hasReportContent && (
              <Button
                className="report-analysis-change report-analysis-clear"
                onClick={clearReport}
              >
                <Trash2 size={14} aria-hidden="true" />
                Clear report
              </Button>
            )}
            <Button className="report-analysis-generate" onClick={generateReport} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" /> Generating report
                </>
              ) : (
                <>
                  {activeWorkbook ? "Run again" : "Generate report"}
                  <ArrowRight size={16} aria-hidden="true" />
                </>
              )}
            </Button>
          </div>
        </>
      )}

      {streamError && <div className="report-analysis-error">{streamError}</div>}

      {outputType === "html" && (htmlResponse || isGenerating) && (
        <div className="report-analysis-html-output">
          {htmlResponse ? (
            !isMAndAReport && HtmlRenderer ? (
              <HtmlRenderer htmlString={htmlResponse} isStreaming={isGenerating} />
            ) : (
              parse(htmlResponse)
            )
          ) : (
            "Preparing textual analysis…"
          )}
        </div>
      )}

      {outputType === "spreadsheet" && isGenerating && !activeWorkbook && (
        <div className="report-workbook-preparing">
          <span className="report-workbook-preparing-icon">
            <Table2 size={19} aria-hidden="true" />
          </span>
          <div>
            <strong>Preparing workbook</strong>
            <p>
              {isMAndAReport
                ? "Setting up the accretion / dilution model…"
                : "Setting up the credit screening workbook…"}
            </p>
          </div>
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {activeWorkbook && outputType === "spreadsheet" && (
        <WorkbookViewer
          workbook={activeWorkbook}
          activeSheetIndex={reportState.activeSheetIndex}
          onSelectSheet={(sheetIndex) =>
            setReportState((previous) => ({ ...previous, activeSheetIndex: sheetIndex }))
          }
          onSheetChange={handleSheetChange}
          onDownload={downloadWorkbook}
        />
      )}
    </div>
  );
};

export default ReportAnalysis;
